import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';

const Privacy: React.FC = () => {
  const theme = useTheme();

  const sectionHeading = {
    fontSize: '24px',
    fontWeight: 700,
    mb: 2,
    color: theme.palette.text.primary,
  } as const;

  const body = {
    fontSize: '16px',
    lineHeight: 1.7,
    color: theme.palette.text.secondary,
    mb: 2,
  } as const;

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, py: 4 }}>
      {/* Hero */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '36px', sm: '48px' },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 2,
            color: theme.palette.text.primary,
            letterSpacing: '-0.02em',
          }}
        >
          Privacy Policy
        </Typography>
        <Typography sx={{ fontSize: '18px', color: theme.palette.text.secondary, fontWeight: 500 }}>
          What emotify collects, why, and the choices you have. Last updated: July 2026.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {/* Overview */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>The short version</Typography>
          <Typography sx={body}>
            emotify is a free tool that visualizes the emotional landscape of a public music playlist.
            There is no account, no login, and no connection to your personal Spotify, YouTube Music or Apple Music profile —
            you simply paste a link to a <em>public</em> playlist and we build a heatmap from it. We keep the
            data we handle to the minimum needed to make that work, plus anonymous usage statistics and
            advertising. This page explains the details.
          </Typography>
        </Box>

        {/* What we process */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Information we process</Typography>
          <Typography sx={body}>
            <strong>Playlist links you submit.</strong> When you paste a Spotify, YouTube Music or Apple Music playlist URL,
            our backend service reads that public playlist to get its track list and then looks up each track's
            audio features (such as valence and energy) to draw the heatmap. We do not need — and never ask for —
            your login credentials, and we cannot see any of your private or personal playlists.
          </Typography>
          <Typography sx={body}>
            <strong>Anonymous usage analytics.</strong> We use Google Analytics to understand aggregate traffic
            (for example, page views and rough visitor counts) so we can improve the tool. This data is anonymous
            and is not used to identify you.
          </Typography>
          <Typography sx={body}>
            <strong>Vibecheck daily game.</strong> When you finish a Vibecheck puzzle, your browser sends a random,
            anonymous player ID (generated on your device, not linked to your identity) together with your guess count
            and whether you solved it. We use it only to avoid counting a result twice and to show daily statistics such
            as "better than X% of players". These records are deleted after a few days; only the aggregate daily counts
            are kept. Your streak and in-progress game are stored in your own browser.
          </Typography>
          <Typography sx={body}>
            <strong>Anonymous product telemetry.</strong> When a playlist is analyzed, we record a small,
            non-identifying event (for example, that an analysis happened, the platform used, and the track count)
            so we can show aggregate stats like the total number of playlists analyzed. These events are not linked
            to you personally.
          </Typography>
          <Typography sx={body}>
            <strong>Local browser storage.</strong> We store a small amount of data in your browser (such as your
            light/dark theme preference and the ID of the last playlist you viewed) so the app remembers your
            choices. This never leaves your device except as part of the playlist lookup described above.
          </Typography>
        </Box>

        {/* Advertising */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Advertising and cookies</Typography>
          <Typography sx={body}>
            emotify is supported by advertising. We use Google AdSense to display ads, and third-party vendors
            including Google use cookies to serve ads based on a user's prior visits to this and other websites.
          </Typography>
          <Typography sx={body}>
            Google's use of advertising cookies enables it and its partners to serve ads to you based on your
            visits to emotify and/or other sites on the Internet. You may opt out of personalized advertising by
            visiting{' '}
            <Typography
              component="a"
              href="https://www.google.com/settings/ads"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none' }}
            >
              Google Ads Settings
            </Typography>
            . You can also opt out of a third party's use of cookies for personalized advertising by visiting{' '}
            <Typography
              component="a"
              href="https://www.aboutads.info"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none' }}
            >
              www.aboutads.info
            </Typography>
            .
          </Typography>
        </Box>

        {/* Third parties */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Third-party services</Typography>
          <Typography sx={body}>
            To provide emotify, we rely on a few third-party services, each governed by its own privacy policy:
            Spotify, YouTube Music and Apple Music (public playlist data), audio-feature data providers, Google Analytics,
            Google AdSense, and our cloud hosting provider (Amazon Web Services). We encourage you to review
            their policies to understand how they handle data.
          </Typography>
        </Box>

        {/* Your choices */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Your choices</Typography>
          <Typography sx={body}>
            You can browse emotify without providing any personal information. You can clear the data stored in
            your browser at any time through your browser settings, block cookies, or use an ad blocker. Because
            we don't collect account information, there is no profile to delete.
          </Typography>
        </Box>

        {/* Children */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Children's privacy</Typography>
          <Typography sx={body}>
            emotify is not directed to children under 13, and we do not knowingly collect personal information
            from them.
          </Typography>
        </Box>

        {/* Contact */}
        <Box>
          <Typography variant="h5" sx={sectionHeading}>Changes and contact</Typography>
          <Typography sx={body}>
            We may update this policy as the tool evolves; material changes will be reflected by the "last updated"
            date above. Questions about privacy? Reach us via the{' '}
            <Link to="/contact" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Contact page</Link>{' '}
            or at{' '}
            <Typography
              component="a"
              href="mailto:emotify.dev@gmail.com"
              sx={{ color: theme.palette.primary.main, fontWeight: 600, textDecoration: 'none' }}
            >
              emotify.dev@gmail.com
            </Typography>
            .
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Privacy;
